import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Phone, MapPin, Clock, Send, Mail, Building2, Award } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedPrivacy) {
      toast.error(language === 'pt' 
        ? 'Por favor, aceite a Política de Privacidade para continuar.' 
        : 'Please accept the Privacy Policy to continue.');
      return;
    }
    
    setIsSubmitting(true);

    // Simulate form submission (mock)
    await new Promise(resolve => setTimeout(resolve, 1500));

    toast.success(t.contact.form.success);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setAcceptedPrivacy(false);
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-teal-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Contacto
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-slate-600">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-8 border border-slate-100">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-medium">
                      {t.contact.form.name}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.contact.form.namePlaceholder}
                      required
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500 focus:ring-teal-500 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      {t.contact.form.email}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.contact.form.emailPlaceholder}
                      required
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500 focus:ring-teal-500 transition-colors"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    {t.contact.form.phone}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.contact.form.phonePlaceholder}
                    className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500 focus:ring-teal-500 transition-colors"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 font-medium">
                    {t.contact.form.message}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.contact.form.messagePlaceholder}
                    required
                    rows={5}
                    className="bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500 focus:ring-teal-500 transition-colors resize-none"
                  />
                </div>

                {/* Privacy Policy Checkbox */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={acceptedPrivacy}
                    onCheckedChange={setAcceptedPrivacy}
                    className="mt-0.5 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                  />
                  <label 
                    htmlFor="privacy" 
                    className="text-sm text-slate-600 cursor-pointer leading-relaxed"
                  >
                    {language === 'pt' ? 'Li e aceito a ' : 'I have read and accept the '}
                    <Link 
                      to="/politica-privacidade" 
                      className="text-teal-600 hover:text-teal-700 underline font-medium"
                      target="_blank"
                    >
                      {language === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
                    </Link>
                  </label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !acceptedPrivacy}
                  className="w-full h-14 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-lg font-medium transition-all hover:shadow-lg hover:shadow-teal-600/25 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {language === 'pt' ? 'A enviar...' : 'Sending...'}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      {t.contact.form.submit}
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Info Card */}
            <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Contabilidade e Fiscalidade, Lda</h4>
                  <div className="flex items-center gap-2 text-teal-100">
                    <Award className="w-4 h-4" />
                    <span className="text-sm">Contabilista Certificado</span>
                  </div>
                  <p className="text-sm text-teal-100 mt-1">(N.º OCC a indicar)</p>
                </div>
              </div>
            </div>

            {/* Phone Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">
                    {language === 'pt' ? 'Telefone' : 'Phone'}
                  </h4>
                  <a
                    href={`tel:${t.contact.info.phone.replace(/\s/g, '')}`}
                    className="text-teal-600 hover:text-teal-700 font-medium text-lg transition-colors"
                  >
                    {t.contact.info.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Email Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                  <a
                    href="mailto:info@cflda.pt"
                    className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
                  >
                    info@cflda.pt
                  </a>
                </div>
              </div>
            </div>

            {/* Address Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">
                    {language === 'pt' ? 'Morada' : 'Address'}
                  </h4>
                  <p className="text-slate-600">
                    {t.contact.info.address}
                  </p>
                </div>
              </div>
            </div>

            {/* Hours Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">
                    {language === 'pt' ? 'Horário' : 'Hours'}
                  </h4>
                  <p className="text-slate-600">
                    {t.contact.info.hours}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
